import { Component } from '@theme/component';

/**
 * @typedef {Object} AxPatentsRefs
 * @property {HTMLElement} [title] - Section heading (carries the per-view wording in data attributes)
 * @property {HTMLElement} [tablist] - Top-level view tablist container (Brevets / Marques)
 * @property {HTMLButtonElement[]} [tabs] - View tab buttons
 * @property {HTMLElement[]} [panels] - View panels (patents, trademarks)
 * @property {HTMLElement} [filterTablist] - Category filter bar container
 * @property {HTMLButtonElement[]} [filterTabs] - Category filter buttons
 * @property {HTMLElement[]} [rows] - Patent table rows (each carries data-categories)
 * @property {HTMLElement} [emptyState] - "No results" message
 * @property {HTMLElement} [status] - aria-live region for the result-count announcement
 */

/**
 * IP repository section.
 *
 * Two top-level views (patents table + trademarks grid) switched by a tablist, plus
 * a category filter that toggles patent-row visibility within the patents view (it
 * filters rows — it does NOT switch panels).
 *
 * Extends Component directly (no Swiper / Section Rendering API needed — all data is
 * rendered server-side from metaobjects). Reimplements the small indicator + ARIA +
 * keyboard-nav bits that ax-tabbed-base would otherwise provide, parameterised so a
 * single set of helpers drives both tab bars. Reuses the `.tablist-nav` stylesheet
 * for the sliding underline indicator.
 *
 * @extends {Component<AxPatentsRefs>}
 */
class AxPatents extends Component {
  /** Index of the trademarks view in the top-level tablist. @type {number} */
  static TRADEMARKS_VIEW = 1;

  /** @type {string[]} */
  requiredRefs = [];

  /** Active top-level view index. @type {number} */
  #activeView = 0;

  /** Active category filter index. @type {number} */
  #activeFilter = 0;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#updateIndicator(this.refs.tablist, this.refs.tabs, this.#activeView);
    this.#updateIndicator(this.refs.filterTablist, this.refs.filterTabs, this.#activeFilter);
    this.#scheduleIndicatorRecalc();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /**
   * Top-level view switch (Brevets / Marques) — real panel show/hide.
   * Delegated from tablist-nav via on:click="/handleViewClick/{index}".
   *
   * @param {number} index
   * @param {Event} [_event]
   */
  handleViewClick(index, _event) {
    if (typeof index !== 'number' || index === this.#activeView) return;

    const { tabs, panels } = this.refs;
    if (!tabs || !panels) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.hidden = i !== index;
    }

    this.#activeView = index;
    this.#updateTitle(index);
    this.#updateIndicator(this.refs.tablist, tabs, index);

    // The filter bar's indicator can't be measured while the patents panel is
    // [hidden] (offsetWidth = 0). Recompute it as the panel becomes visible again.
    if (index === 0) {
      this.#updateIndicator(this.refs.filterTablist, this.refs.filterTabs, this.#activeFilter);
    }
  }

  /**
   * Keyboard navigation for the view tablist.
   * @param {KeyboardEvent} event
   */
  handleViewKeydown(event) {
    const next = this.#arrowTarget(event, this.refs.tabs, this.#activeView);
    if (next === null) return;

    this.refs.tabs?.[next]?.focus();
    this.handleViewClick(next, event);
  }

  /**
   * Category filter — toggles patent-row visibility, does NOT switch panels.
   * Delegated from tablist-nav via on:click="/handleFilterClick/{index}".
   *
   * @param {number} index
   * @param {Event} [_event]
   */
  handleFilterClick(index, _event) {
    if (typeof index !== 'number') return;

    const { filterTabs, rows } = this.refs;
    if (!filterTabs || !rows) return;

    for (const [i, tab] of filterTabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    const category = this.#tabSlug(filterTabs[index]) || 'all';
    let visible = 0;

    for (const row of rows) {
      const categories = (row.dataset.categories ?? '').split(' ');
      const show = category === 'all' || categories.includes(category);
      row.hidden = !show;
      if (show) visible += 1;
    }

    this.#activeFilter = index;
    this.#updateIndicator(this.refs.filterTablist, filterTabs, index);
    this.#toggleEmptyState(visible);
    this.#announceCount(visible);
  }

  /**
   * Keyboard navigation for the category filter bar.
   * @param {KeyboardEvent} event
   */
  handleFilterKeydown(event) {
    const next = this.#arrowTarget(event, this.refs.filterTabs, this.#activeFilter);
    if (next === null) return;

    this.refs.filterTabs?.[next]?.focus();
    this.handleFilterClick(next, event);
  }

  /**
   * Shared Arrow/Home/End resolver. Returns the target index, or null when the key
   * isn't a navigation key.
   *
   * @param {KeyboardEvent} event
   * @param {HTMLButtonElement[] | undefined} tabs
   * @param {number} current
   * @returns {number | null}
   */
  #arrowTarget(event, tabs, current) {
    if (!tabs?.length) return null;

    const total = tabs.length;

    /** @type {Record<string, number>} */
    const navMap = {
      ArrowLeft: -1,
      ArrowRight: 1,
      Home: -current,
      End: total - 1 - current,
    };

    const offset = navMap[event.key];
    if (offset === undefined) return null;

    event.preventDefault();
    return (current + offset + total) % total;
  }

  /**
   * Swap the section heading between the patents and trademarks wording.
   *
   * No-op unless the merchant filled both titles: the section only emits the two
   * data attributes when the trademarks title differs from the patents one, so a
   * single-title setup keeps a static heading.
   *
   * @param {number} index
   */
  #updateTitle(index) {
    const title = this.refs.title;
    const { titlePatents, titleTrademarks } = title?.dataset ?? {};
    if (!title || !titlePatents || !titleTrademarks) return;

    title.textContent = index === AxPatents.TRADEMARKS_VIEW ? titleTrademarks : titlePatents;
  }

  /**
   * Slide the active indicator under the given tab. Mirrors ax-tabbed-base's
   * #updateIndicator but parameterised so one method serves both bars.
   *
   * @param {HTMLElement | undefined} tablist
   * @param {HTMLButtonElement[] | undefined} tabs
   * @param {number} index
   */
  #updateIndicator(tablist, tabs, index) {
    if (!tablist || !tabs?.length) return;

    const tab = tabs[index];
    if (!tab) return;

    tablist.style.setProperty('--indicator-left', `${tab.offsetLeft}px`);
    tablist.style.setProperty('--indicator-width', `${tab.offsetWidth}px`);
  }

  /**
   * Re-measure both indicators once fonts have resolved and on tablist resize
   * (iOS Safari mis-measures before web fonts load).
   */
  #scheduleIndicatorRecalc() {
    const recompute = () => {
      this.#updateIndicator(this.refs.tablist, this.refs.tabs, this.#activeView);
      this.#updateIndicator(this.refs.filterTablist, this.refs.filterTabs, this.#activeFilter);
    };

    if (document.fonts?.ready) {
      document.fonts.ready.then(recompute);
    }

    if (!('ResizeObserver' in window)) return;

    this.#resizeObserver = new ResizeObserver(recompute);

    if (this.refs.tablist) this.#resizeObserver.observe(this.refs.tablist);
    if (this.refs.filterTablist) this.#resizeObserver.observe(this.refs.filterTablist);
  }

  /**
   * Extract the slug baked into a tab button id (`Tab-{slug}-{sectionId}`).
   *
   * @param {HTMLButtonElement | undefined} button
   * @returns {string}
   */
  #tabSlug(button) {
    const id = button?.id ?? '';
    const prefix = 'Tab-';
    const suffix = `-${this.dataset.sectionId ?? ''}`;

    if (id.startsWith(prefix) && id.endsWith(suffix)) {
      return id.slice(prefix.length, id.length - suffix.length);
    }

    return '';
  }

  /**
   * @param {number} visible
   */
  #toggleEmptyState(visible) {
    if (this.refs.emptyState) this.refs.emptyState.hidden = visible !== 0;
  }

  /**
   * Announce the visible-row count in the polite live region. The localized
   * template carries a `%count%` sentinel (Liquid leaves `{{ }}` placeholders
   * untouched only inside `t:`, so a literal sentinel is safer).
   *
   * @param {number} visible
   */
  #announceCount(visible) {
    const status = this.refs.status;
    if (!status) return;

    const template = status.dataset.countTemplate ?? '%count%';
    status.textContent = template.replace('%count%', String(visible));
  }
}

if (!customElements.get('ax-patents-component')) {
  customElements.define('ax-patents-component', AxPatents);
}
